/* eslint-disable react-refresh/only-export-components */
// Disabled: This is a standard React Context pattern - exporting Provider + hook together

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useAuth as useClerkAuth, useUser } from '@clerk/clerk-react';
import { setTokenGetter } from '../services/api';

/**
 * User information sourced from Clerk
 */
export interface AuthUser {
  id: string;
  email: string;
  name: string;
}

/**
 * Authentication context state
 */
export interface AuthContextType {
  /** Current authenticated user */
  user: AuthUser | null;
  /** Whether user is authenticated */
  isAuthenticated: boolean;
  /** Whether authentication is in progress */
  isLoading: boolean;
  /** Current access token for API calls */
  accessToken: string | null;
  /** Initiate login flow */
  login: () => Promise<void>;
  /** Initiate logout flow */
  logout: () => Promise<void>;
  /** Get fresh access token for API calls */
  getAccessToken: () => Promise<string | null>;
  /** Error during authentication */
  error: Error | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Authentication Context Provider
 *
 * Provides authentication state and methods to child components.
 * Wraps Clerk React SDK hooks in a provider-agnostic interface so that
 * consuming components never import `@clerk/clerk-react` directly.
 */
export const AuthContextProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isSignedIn, isLoaded, getToken, signOut } = useClerkAuth();
  const { user: clerkUser } = useUser();

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [dbUserId, setDbUserId] = useState<string | null>(null);

  const isAuthenticated = Boolean(isSignedIn);
  const isLoading = !isLoaded;

  const user: AuthUser | null =
    isSignedIn && clerkUser
      ? {
          id: dbUserId || clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress ?? '',
          name: clerkUser.fullName ?? clerkUser.firstName ?? 'User',
        }
      : null;

  /**
   * Acquire a session token from Clerk for API calls.
   */
  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      const token = await getToken();
      setAccessToken(token);
      return token;
    } catch (err) {
      console.warn('Token acquisition failed:', err);
      return null;
    }
  }, [getToken]);

  const login = useCallback(async () => {
    window.location.href = '/sign-in';
  }, []);

  const logout = useCallback(async () => {
    try {
      setError(null);
      setAccessToken(null);
      await signOut();
    } catch (logoutError) {
      console.error('Logout failed:', logoutError);
      setError(logoutError as Error);
    }
  }, [signOut]);

  /**
   * Hydrate the user's display name from the backend profile
   * (the DB may have a better name than Clerk's default).
   */
  useEffect(() => {
    if (!isAuthenticated || !clerkUser) return;

    let cancelled = false;

    getAccessToken().then(async (token) => {
      if (!token || cancelled) return;
      try {
        const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        const res = await fetch(`${baseUrl}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const profile = await res.json();
          if (!cancelled && profile?.id) {
            setDbUserId(profile.id);
          }
        }
      } catch {
        // Non-critical – Clerk name serves as fallback
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, clerkUser, getAccessToken]);

  /** Wire the token getter into the API client interceptor. */
  useEffect(() => {
    setTokenGetter(getAccessToken);
  }, [getAccessToken]);

  const value: AuthContextType = {
    user,
    isAuthenticated,
    isLoading,
    accessToken,
    login,
    logout,
    getAccessToken,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook to access authentication context
 *
 * @throws Error if used outside AuthContextProvider
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthContextProvider');
  }
  return context;
};

export default AuthContext;
